/**
 * Editor de etapas (stages) de uma pipeline
 */

import { useEffect, useState } from 'react';
import { Check, GripVertical, Loader2, Trash2 } from 'lucide-react';
import { Input, message, Popconfirm, Popover } from 'antd';
import { usePipelineStages, STAGE_COLOR_PRESETS } from '../hooks/usePipelineStages';
import { PipelineStage } from '../types';

interface PipelineStagesEditorProps {
  pipelineId: string | null;
}

export function PipelineStagesEditor({ pipelineId }: PipelineStagesEditorProps) {
  const [newStageName, setNewStageName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [orderedStages, setOrderedStages] = useState<PipelineStage[]>([]);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const {
    stages,
    loading,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,
  } = usePipelineStages(pipelineId);

  useEffect(() => {
    setOrderedStages(stages);
  }, [stages]);

  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    setDraggedId(id);
  };

  const handleDragOver = (e: React.DragEvent, overId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === overId) return;

    setOrderedStages((prev) => {
      const fromIndex = prev.findIndex((s) => s.id === draggedId);
      const toIndex = prev.findIndex((s) => s.id === overId);
      if (fromIndex === -1 || toIndex === -1) return prev;

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleDragEnd = () => {
    if (draggedId) {
      reorderStages.mutate(
        orderedStages.map((s) => s.id),
        { onError: (e) => message.error(e.message) }
      );
    }
    setDraggedId(null);
  };

  const handleCreate = async () => {
    const nome = newStageName.trim();
    if (!nome || !pipelineId) return;

    try {
      await createStage.mutateAsync({ nome });
      setNewStageName('');
      message.success('Etapa criada');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Erro ao criar etapa');
    }
  };

  const startEdit = (id: string, nome: string) => {
    setEditingId(id);
    setEditName(nome);
  };

  const saveEdit = async (id: string) => {
    const nome = editName.trim();
    if (!nome) return;

    try {
      await updateStage.mutateAsync({ id, nome });
      setEditingId(null);
      message.success('Etapa atualizada');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Erro ao atualizar');
    }
  };

  const handleColorChange = async (id: string, cor: string) => {
    try {
      await updateStage.mutateAsync({ id, cor });
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Erro ao alterar cor');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteStage.mutateAsync(id);
      message.success('Etapa removida');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Erro ao remover');
    }
  };

  if (!pipelineId) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <p className="text-xs text-gray-400 text-center">Selecione uma pipeline</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          Etapas
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-2 min-h-0">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : orderedStages.length === 0 ? (
          <p className="px-3 py-2 text-xs text-gray-400">
            Nenhuma etapa. Adicione abaixo.
          </p>
        ) : (
          <ul className="space-y-1">
            {orderedStages.map((stage) => (
              <li
                key={stage.id}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDrop={(e) => e.preventDefault()}
                className={`group flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-zinc-800 ${
                  draggedId === stage.id ? 'opacity-40' : ''
                }`}
              >
                <button
                  type="button"
                  draggable
                  onDragStart={(e) => handleDragStart(e, stage.id)}
                  onDragEnd={handleDragEnd}
                  className="flex-shrink-0 p-0.5 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 cursor-grab active:cursor-grabbing"
                  title="Arrastar para reordenar"
                >
                  <GripVertical className="w-3.5 h-3.5" />
                </button>

                <ColorPicker
                  color={stage.cor || STAGE_COLOR_PRESETS[0]}
                  onChange={(cor) => handleColorChange(stage.id, cor)}
                />

                {editingId === stage.id ? (
                  <Input
                    size="small"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onPressEnter={() => saveEdit(stage.id)}
                    onBlur={() => saveEdit(stage.id)}
                    autoFocus
                    className="flex-1 text-xs"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => startEdit(stage.id, stage.nome)}
                    className="flex-1 text-left text-sm text-gray-800 dark:text-gray-200 truncate"
                  >
                    {stage.nome}
                  </button>
                )}

                <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Popconfirm
                    title="Remover esta etapa?"
                    description="Só é possível se não houver cards."
                    onConfirm={() => handleDelete(stage.id)}
                    okText="Remover"
                    cancelText="Cancelar"
                  >
                    <button
                      type="button"
                      className="p-0.5 rounded text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </Popconfirm>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="p-3 border-t border-gray-100 dark:border-zinc-800">
        <div className="flex items-center gap-1">
          <Input
            placeholder="Nova etapa"
            value={newStageName}
            onChange={(e) => setNewStageName(e.target.value)}
            onPressEnter={handleCreate}
            size="small"
            className="text-xs"
            disabled={createStage.isPending}
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={!newStageName.trim() || createStage.isPending}
            className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded text-green-600 hover:bg-green-50 disabled:opacity-40"
          >
            {createStage.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ColorPicker({
  color,
  onChange,
}: {
  color: string;
  onChange: (cor: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      content={
        <div className="grid grid-cols-4 gap-1.5 w-[104px]">
          {STAGE_COLOR_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => {
                onChange(preset);
                setOpen(false);
              }}
              className={`w-5 h-5 rounded-full border-2 ${
                preset === color ? 'border-gray-800 dark:border-white' : 'border-transparent'
              }`}
              style={{ backgroundColor: preset }}
            />
          ))}
        </div>
      }
    >
      <button
        type="button"
        className="flex-shrink-0 w-4 h-4 rounded-full border border-gray-200 dark:border-zinc-600"
        style={{ backgroundColor: color }}
        title="Alterar cor"
      />
    </Popover>
  );
}

export default PipelineStagesEditor;
